import {
  useState,
  Fragment,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  InputHTMLAttributes,
} from 'react'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  ColumnDef,
  Header,
  CellContext,
  Row,
  Column,
  Table as TableType,
} from '@tanstack/react-table'
import { useMultiSelectDraggable } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectDraggable'

import debounce from 'lodash.debounce'

import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils'
import { useAnnotationLayer } from './Annotations/queries'
import { useDocumentState } from '@/state/document'
import { Spinner } from '@/components/ui/spinner'
import { usePrevious } from '@/utils/hooks'
import {
  TemplateAttribute,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'
import { Textarea } from '@/components/ui/textarea'
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@/components/ui/popover'
import { FlattenedDocumentPage } from '@/lib/api/client'
import { getAbsoluteYFromSubPage } from './Annotations/utils'
import { OptionsContextMenu } from '@/components/core/OptionsContextMenu'
import { useKeyboardNudge } from '@/utils/hooks/useKeyboardNudge'
import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'
import { useIsTemplateAttributeMultiSelected } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useIsTemplateAttributeMultiSelected'
import { useMultiSelectHandler } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectHandler'

export const createNewColumn = ({
  accessorKey,
  onCellChange,
  selectTable,
  isReadOnly,
  documentPageId,
  tableType,
}: {
  accessorKey: string
  onCellChange: (
    rowIndex: number,
    columnId: string | number,
    value: string,
  ) => void
  selectTable: () => void
  isReadOnly: boolean
  documentPageId: string
  tableType: 'parts-table' | 'tools-table' | 'generic-table'
}): ColumnDef<TableData> => ({
  accessorKey,
  header: '',
  enableResizing: true,
  size: 100,
  cell: (cellProps) => (
    <EditableTableCell
      {...cellProps}
      isReadOnly={isReadOnly}
      documentPageId={documentPageId}
      onChange={onCellChange}
      selectTable={selectTable}
      tableType={tableType}
    />
  ),
})

type TableData = {
  [accessor: string]: any
}
type ColumnsData = {
  [accessor: string]: any
}
type SaveTableDataOptions = {
  rowIndex: number
  isCustomRow: boolean
}
type EditableTableProps = {
  saveTableData: (options?: SaveTableDataOptions) => void | Promise<void>
  onCellChange: (
    rowIndex: number,
    columnId: string | number,
    value: string,
  ) => void
  removeRow?: (rowIndex: number) => void
  addRow?: (options: {
    newRow: TableData
    rowIndex?: number
    position: 'above' | 'below'
  }) => void
  onHeaderChange: (value: string, accessorKey: string) => void
  saveCustomColumns: () => void
  removeColumn?: (accessorKey: string) => Promise<void>
  addColumn?: (options: {
    accessorKey: string
    newColumn: ColumnDef<TableData>
    position: 'left' | 'right'
  }) => void
  setSelectedRow?: (rowIndex: number | null) => void
  setSelectedColumn?: (accessor: string | null) => void
  selectedRow: number | null
  selectedColumn: string | null
  documentPage: FlattenedDocumentPage
  tableData: Array<TableData>
  defaultRows?: number
  columnsDef: Array<ColumnDef<TableData>>
  columnsData: ColumnsData
  tableClasses?: string
  id: string
  isReadOnly?: boolean
  type: 'parts-table' | 'tools-table' | 'generic-table'
  positionData?: { x: number; y: number; z?: number }
  isDeleting?: boolean
  direction?: 'right' | 'left'
  columnsSize: {
    [accessor: string]: number
  }
  tableTemplate: TemplateAttribute<any>
  testId?: string
  suggestions?: {
    [accessor: string]: Array<string>
  }
  headerSuggestions?: Record<string, string>
  notCustomColumns?: Array<string>
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}
export const EditableTable = ({
  saveTableData,
  onCellChange,
  removeRow,
  addRow,
  onHeaderChange,
  saveCustomColumns,
  removeColumn,
  addColumn,
  setSelectedRow,
  setSelectedColumn,
  selectedRow,
  selectedColumn,
  documentPage,
  tableData,
  defaultRows = tableData.length,
  columnsDef,
  columnsData,
  tableClasses = '',
  id,
  isReadOnly = false,
  type,
  positionData,
  isDeleting = false,
  direction = 'left',
  columnsSize,
  tableTemplate,
  testId = 'editable-table',
  suggestions,
  headerSuggestions,
  notCustomColumns,
  onNudgeStart,
  onNudgeEnd,
}: EditableTableProps) => {
  const INITIAL_DELTA = useMemo(() => ({ x: 0, y: 0 }), [])
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const croppingImageId = useDocumentState((state) => state.croppingImageId)
  const zoom = useDocumentState((state) => state.zoom)
  const zoomRatio = useMemo(() => zoom / 100, [zoom])
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const documentPageId = useMemo(
    () => documentPage.id as string,
    [documentPage],
  )
  const templateId = useMemo(() => tableTemplate?.id as string, [tableTemplate])
  const isSelected = useMemo(
    () =>
      selectedElement?.documentPageId === documentPageId &&
      (selectedElement?.id === id || selectedElement?.id === templateId),
    [selectedElement, documentPageId, id, templateId],
  )

  const [isResizing, setIsResizing] = useState(false)
  const [{ position, delta }, setPosition] = useState({
    position: positionData,
    delta: INITIAL_DELTA,
  })
  const tableDataSnapshot = useMemo(
    () => JSON.stringify(tableData),
    [tableData],
  )
  const latestTableDataSnapshotRef = useRef(tableDataSnapshot)
  const lastSavedTableDataSnapshotRef = useRef(tableDataSnapshot)
  const saveTableDataRef = useRef(saveTableData)
  const pendingSaveOptionsRef = useRef<SaveTableDataOptions | undefined>(
    undefined,
  )
  const isSaveInFlightRef = useRef(false)
  const hasQueuedSaveRef = useRef(false)

  const subPageId = useMemo(
    () => tableTemplate?.template_values?.subPageId || documentPageId,
    [tableTemplate, documentPageId],
  )
  const documentSubPages = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id,
      ],
    [documentPage],
  )

  const tableRef = useRef<HTMLTableElement>(null)
  const resizerRef = useRef(null)

  useEffect(() => {
    latestTableDataSnapshotRef.current = tableDataSnapshot
  }, [tableDataSnapshot])

  useEffect(() => {
    saveTableDataRef.current = saveTableData
  }, [saveTableData])

  const flushQueuedSave = useCallback(async () => {
    if (isSaveInFlightRef.current) {
      // Serialize saves: if one is in-flight, mark another pass and return.
      hasQueuedSaveRef.current = true
      return
    }

    const latestSnapshot = latestTableDataSnapshotRef.current
    if (latestSnapshot === lastSavedTableDataSnapshotRef.current) {
      // Drop no-op saves when table data has not changed since last successful save.
      hasQueuedSaveRef.current = false
      return
    }

    isSaveInFlightRef.current = true
    hasQueuedSaveRef.current = false

    try {
      await saveTableDataRef.current(pendingSaveOptionsRef.current)
      lastSavedTableDataSnapshotRef.current = latestSnapshot
    } finally {
      isSaveInFlightRef.current = false
      if (hasQueuedSaveRef.current) {
        void flushQueuedSave()
      }
    }
  }, [])

  const queueTableSave = useMemo(
    () =>
      debounce(() => {
        void flushQueuedSave()
      }, 250),
    [flushQueuedSave],
  )

  useEffect(() => {
    return () => {
      queueTableSave.cancel()
    }
  }, [queueTableSave])

  const saveTableDataWithQueue = useCallback(
    (options?: SaveTableDataOptions) => {
      // Keep only the most recent save intent; batch bursts via debounce.
      pendingSaveOptionsRef.current = options
      hasQueuedSaveRef.current = true
      queueTableSave()
    },
    [queueTableSave],
  )

  const selectTable = useCallback(() => {
    const selectedId = templateId || id
    const isAlreadySelected =
      selectedElement?.documentPageId === documentPageId &&
      selectedElement?.id === selectedId &&
      selectedElement?.type === type &&
      selectedElement?.isRemovable === false

    if (isAlreadySelected) {
      // Avoid global selection store updates on repeated cell clicks.
      return
    }

    setSelectedElement({
      documentPageId,
      id: selectedId,
      isRemovable: false,
      type,
    })
  }, [
    documentPageId,
    templateId,
    id,
    type,
    selectedElement,
    setSelectedElement,
  ])

  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
  } = useAnnotationLayer(documentPageId)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId,
  })

  const {
    attributes,
    isDragging,
    listeners,
    transform,
    setNodeRef,
    setActivatorNodeRef,
  } = useMultiSelectDraggable({
    disabled: isResizing || isAddingElement || isReadOnly || !!croppingImageId,
    id: templateId || id,
    data: { type, data_type: 'table' },
  })

  const { bind, previewDelta } = useKeyboardNudge({
    isActive: isSelected && Boolean(selectedElement?.isRemovable),
    showGridOnNudge: true,
    onNudgeStart,
    onNudgeEnd,
    template: tableTemplate,
    documentPage: documentPage as any,
  })

  useEffect(() => {
    const tableContainer = tableRef.current
    if (tableContainer) {
      setNodeRef(tableContainer)
    }
  }, [tableRef, setNodeRef])

  useEffect(() => {
    const tX = transform?.x ?? 0
    const tY = transform?.y ?? 0
    const pX = previewDelta?.x ?? 0
    const pY = previewDelta?.y ?? 0

    // If everything is 0, this is typically the "drag ended" / "idle" state.
    // Don't overwrite delta here — let it be reset only when positionData changes.
    if (tX === 0 && tY === 0 && pX === 0 && pY === 0) {
      return
    }

    setPosition((prevPosition) => ({
      ...prevPosition,
      delta: {
        x: (tX + pX) / zoomRatio,
        y: (tY + pY) / zoomRatio,
      },
    }))
  }, [transform, previewDelta, zoomRatio])

  useEffect(() => {
    setPosition({
      position: positionData,
      delta: INITIAL_DELTA,
    })
  }, [INITIAL_DELTA, positionData])

  const getNewColumnAccessor = useCallback(() => {
    const accessor = `custom-column_${documentPageId}_${Math.floor(
      Math.random() * 1000,
    )}`
    if (columnsData[accessor]) return getNewColumnAccessor()
    return accessor
  }, [columnsData, documentPageId])

  const addNewColumn = useCallback(
    async ({
      accessorKey,
      position,
      newAccessorKey,
    }: {
      accessorKey: string
      position: 'left' | 'right'
      newAccessorKey?: string
    }) => {
      if (!addColumn) return

      const newAccessor = newAccessorKey ?? getNewColumnAccessor()
      const newColumn = createNewColumn({
        accessorKey: newAccessor,
        onCellChange,
        selectTable,
        isReadOnly: isReadOnly || !!headerSuggestions?.[accessorKey],
        documentPageId,
        tableType: type,
      })
      await addColumn({ accessorKey, newColumn, position })
    },
    [
      headerSuggestions,
      documentPageId,
      isReadOnly,
      addColumn,
      getNewColumnAccessor,
      onCellChange,
      selectTable,
    ],
  )

  const addNewRow = useCallback(
    ({
      rowIndex,
      position,
    }: {
      rowIndex?: number
      position: 'above' | 'below'
    }) => {
      if (!addRow) return
      const newRow = columnsDef.reduce((acc, column) => {
        const accessorKey = (column as { accessorKey: string }).accessorKey
        acc[accessorKey] = ''
        return acc
      }, {}) as TableData

      addRow({
        rowIndex,
        newRow,
        position,
      })
    },
    [columnsDef, addRow],
  )

  const onUpdateColumnsSizes = useCallback(
    debounce(async (updatedSizes: { [accessor: string]: number }) => {
      await updateTemplate({
        payload: {
          type,
          columnsSize: {
            ...(columnsSize || {}),
            ...updatedSizes,
          },
        },
        data_type: 'table' as any,
        templateAttributeId: templateId,
      })
    }, 300),
    [type, templateId, updateTemplate],
  )

  const onResizeColumnHandler = useCallback(
    async ({
      width,
      accessor,
    }: {
      width?: number | string
      accessor: string
    }) => {
      await onUpdateColumnsSizes({
        [accessor]: width as number,
      })
    },
    [onUpdateColumnsSizes],
  )

  const renderEditableTableHeader = useCallback(
    ({ header }: { header: Header<TableData, unknown> }) => {
      const accessorKey = (header.column.columnDef as { accessorKey: string })
        .accessorKey
      const columnKeys = new Set(Object.keys(columnsData))
      const filteredSuggestions = Object.entries(headerSuggestions || {})
        .filter(([key]) => !columnKeys.has(key))
        .map(([key, value]) => ({ key, value }))

      const isSuggestedColumn = !!headerSuggestions?.[accessorKey]
      const inputProps = {
        disabled: true,
        value: columnsData[accessorKey] || '',
        className: 'border-none shadow-none m-0 px-1 py-0 h-full text-xs',
        style: {
          opacity: 1,
          cursor: 'default',
          outline: 'none',
          boxShadow: 'none',
        },
      }

      if (!isSuggestedColumn) {
        inputProps['disabled'] = false
        inputProps['onChange'] = (value) => onHeaderChange(value, accessorKey)
        inputProps['style'] = {
          ...inputProps.style,
          cursor: 'text',
        }
      }

      return (
        <EditableTableHeader
          key={header.id}
          inputProps={inputProps}
          saveChanges={saveCustomColumns}
          columnAccessor={accessorKey}
          onResizeEnd={onResizeColumnHandler}
          selectTable={selectTable}
          isReadOnly={isReadOnly}
          width={header.getSize()}
          isResizing={header.column.getIsResizing()}
          resizeHandler={header.getResizeHandler()}
          canResize={header.column.getCanResize() && !isReadOnly}
          direction={direction}
          table={table}
          tableType={type}
          column={header.column}
          headerSuggestions={filteredSuggestions}
          addNewColumn={addNewColumn}
        />
      )
    },
    [
      onHeaderChange,
      columnsData,
      isReadOnly,
      direction,
      headerSuggestions,
      onResizeColumnHandler,
      saveCustomColumns,
      selectTable,
      addNewColumn,
      removeColumn,
    ],
  )

  const table = useReactTable({
    data: tableData,
    columns: columnsDef,
    columnResizeMode: 'onChange',
    columnResizeDirection: direction === 'right' ? 'rtl' : 'ltr',
    getCoreRowModel: getCoreRowModel(),
    meta: {
      saveTableData: saveTableDataWithQueue,
      setSelectedRow,
      setSelectedColumn,
      selectedRow,
      selectedColumn,
      tableId: id,
    },
  })

  useEffect(() => {
    table.setColumnSizing(columnsSize)
  }, [columnsSize, table])

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e) => {
      if (!tableRef.current) return
      const rect = tableRef.current.getBoundingClientRect()
      const oldTableWidth = rect.width
      const newTableWidth = Math.max(100, e.clientX - rect.left)
      const delta = newTableWidth - oldTableWidth
      const resizableColumns = table
        .getVisibleLeafColumns()
        .filter((column) => column.getCanResize())
      const colCount = resizableColumns?.length

      if (delta !== 0 && colCount > 0) {
        const extraPerCol = delta / colCount

        const updatedSizes = resizableColumns.reduce(
          (accum, column) => {
            const currentSize = column.getSize()
            const accessor = (column.columnDef as { accessorKey: string })
              .accessorKey

            column.columnDef.size = currentSize + extraPerCol

            return {
              ...accum,
              [accessor]: currentSize + extraPerCol,
            }
          },
          {} as {
            [accessor: string]: number
          },
        )

        table.setColumnSizing(updatedSizes)
        onUpdateColumnsSizes(updatedSizes)
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('pointerup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('pointerup', handleMouseUp)
    }
  }, [isResizing])

  const columnOptions = useMemo(() => {
    const options: Array<{
      label: string
      action: () => void
      testId: string
    }> = []

    if (selectedColumn) {
      options.push({
        label: 'Insert column right',
        action: () =>
          addNewColumn({ accessorKey: selectedColumn, position: 'right' }),
        testId: 'insert-column-right',
      })
    }
    if (selectedColumn) {
      options.push({
        label: 'Insert column left',
        action: () =>
          addNewColumn({ accessorKey: selectedColumn, position: 'left' }),
        testId: 'insert-column-left',
      })
    }
    if (selectedColumn && removeColumn) {
      options.push({
        label: 'Remove column',
        action: () => removeColumn(selectedColumn),
        testId: 'remove-column',
      })
    }
    return options
  }, [selectedColumn, columnsDef])

  const rowOptions = useMemo(() => {
    const options: Array<{
      label: string
      action: () => void
      testId: string
    }> = []

    if (selectedRow === -1) {
      options.push({
        label: 'Insert row at the bottom',
        action: () =>
          addNewRow({
            position: 'below',
          }),
        testId: 'insert-row-at-the-bottom',
      })

      return options
    }

    if (selectedRow !== null && selectedRow >= defaultRows) {
      options.push({
        label: 'Insert row above',
        action: () =>
          addNewRow({
            rowIndex: selectedRow,
            position: 'above',
          }),
        testId: 'insert-row-above',
      })
    }

    if (selectedRow !== null && selectedRow >= defaultRows - 1) {
      options.push({
        label: 'Insert row below',
        action: () =>
          addNewRow({
            rowIndex: selectedRow,
            position: 'below',
          }),
        testId: 'insert-row-below',
      })
    }

    if (selectedRow !== null && selectedRow >= defaultRows && removeRow) {
      options.push({
        label: 'Remove row',
        action: () => removeRow(selectedRow),
        testId: 'remove-row',
      })
    }

    return options
  }, [selectedRow, defaultRows])

  const { setDraggableAnnotationParentRef } = useRelativeToAbsolutePositioning()

  const isMultiSelected = useIsTemplateAttributeMultiSelected({
    templateAttributeId: tableTemplate.id,
  })

  const {
    onPointerDown: multiSelectKeydownHandler,
    onPointerUp: multiSelectKeyupHandler,
  } = useMultiSelectHandler()

  return (
    <OptionsContextMenu
      isReadonly={isReadOnly}
      isDisabled={isOptionsMenuLoading}
      onAuxClick={(e) => {
        const target = e.target as HTMLElement
        const columnId = target.getAttribute('data-id-column') || null
        const rowId = target.getAttribute('data-id-row') || null

        setSelectedColumn?.(columnId)
        setSelectedRow?.(rowId ? parseInt(rowId, 10) : null)
      }}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setSelectedColumn?.(null)
          setSelectedRow?.(null)
        }
      }}
      optionsGroups={[
        {
          options: [
            {
              label: 'Bring to front',
              testId: 'bring-to-front',
              action: () => bringToFront(tableTemplate?.id as string),
            },
            {
              label: 'Bring forward',
              testId: 'bring-forward',
              action: () => bringForward(tableTemplate?.id as string),
            },
            {
              label: 'Send backward',
              testId: 'bring-backward',
              action: () => sendBackward(tableTemplate?.id as string),
            },
            {
              label: 'Send to back',
              testId: 'bring-to-back',
              action: () => sendToBack(tableTemplate?.id as string),
            },
          ],
          groupTestId: 'position-options',
        },
        {
          options: columnOptions,
          groupTestId: 'column-options',
        },
        {
          options: rowOptions,
          groupTestId: 'row-options',
        },
      ]}
    >
      <div
        ref={(el) => {
          setActivatorNodeRef(el)
          const parentEl = el?.parentElement as HTMLDivElement | undefined
          if (tableTemplate.id && parentEl) {
            setDraggableAnnotationParentRef(tableTemplate.id, parentEl)
          }
        }}
        {...bind}
        {...attributes}
        {...listeners}
        className={cn('border box-content p-px absolute', {
          'cursor-move': !isReadOnly,
          'cursor-default': isReadOnly,
          'rounded-md': isSelected,
          'shadow-md': isDragging,
          'shadow-gray-200': isDragging,
          'bg-sky-200/10': isDragging,
          'z-[100]': isDragging,
        })}
        style={{
          translate: `${delta?.x || 0}px ${delta?.y || 0}px`,
          top: getAbsoluteYFromSubPage({
            subPageId,
            relativeY: position?.y || 0,
            subPageIds: documentSubPages,
          }),
          [direction]:
            direction === 'right' ? -(position?.x || 0) : position?.x || 0,
          zIndex: positionData?.z || 1000,
          borderColor:
            isSelected || isMultiSelected ? '#818cf8' : 'transparent',
        }}
        data-testid={testId}
        onPointerDownCapture={(e) => {
          multiSelectKeydownHandler(e as any, tableTemplate)
        }}
        onPointerUpCapture={(e) => {
          multiSelectKeyupHandler(e as any)
        }}
      >
        {isDeleting && (
          <div className="absolute h-full w-full bg-zinc-400/60 z-10">
            <Spinner />
          </div>
        )}
        <Table
          overflow="visible"
          ref={tableRef}
          className={cn(
            'border-y border-solid border-gray-200 border-collapse relative',
            tableClasses,
          )}
        >
          <TableHeader className="bg-muted/80">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="w-full">
                {headerGroup.headers.map((header) =>
                  renderEditableTableHeader({
                    header,
                  }),
                )}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row, i) => (
              <DeletableRow
                row={row}
                key={row.id}
                onCellChange={onCellChange}
                notCustomColumns={notCustomColumns}
                isCustomRow={i >= defaultRows}
                selectTable={selectTable}
                documentPageId={documentPageId}
                isReadOnly={isReadOnly}
                suggestions={suggestions}
                tableType={type}
              />
            ))}
          </TableBody>
        </Table>
        {!isReadOnly && (
          <div
            ref={resizerRef}
            data-disableprint
            className="absolute bottom-[2px] right-[2px] w-2 h-2 cursor-col-resize z-10 border-r border-b border-gray-400"
            onMouseDown={(e) => {
              setIsResizing(true)
              e.stopPropagation()
            }}
            onPointerDown={(e) => {
              setIsResizing(true)
              e.stopPropagation()
            }}
          />
        )}
      </div>
    </OptionsContextMenu>
  )
}

type DeletableRowProps = {
  isCustomRow: boolean
  row: Row<TableData>
  isReadOnly?: boolean
  onCellChange: (
    rowIndex: number,
    columnId: string | number,
    value: string,
  ) => void
  selectTable?: () => void
  documentPageId: string
  suggestions?: { [accessor: string]: Array<string> }
  notCustomColumns?: Array<string>
  tableType: 'parts-table' | 'tools-table' | 'generic-table'
}
const DeletableRow = ({
  row,
  isCustomRow,
  isReadOnly,
  onCellChange,
  selectTable,
  documentPageId,
  suggestions,
  notCustomColumns,
  tableType,
}: DeletableRowProps) => {
  return (
    <TableRow
      key={row.id}
      className="w-full relative"
      data-testid={`${tableType}__table-row`}
    >
      {row.getVisibleCells().map((cell) => {
        const accessor = (cell.column.columnDef as any).accessorKey as string
        return (
          <Fragment key={cell.id}>
            {isCustomRow && !notCustomColumns?.includes(accessor) ? (
              <EditableTableCell
                {...cell.getContext()}
                onChange={onCellChange}
                selectTable={selectTable}
                isCustomRow={isCustomRow}
                isReadOnly={isReadOnly}
                documentPageId={documentPageId}
                suggestions={suggestions?.[cell.column.id]}
                tableType={tableType}
              />
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </Fragment>
        )
      })}
    </TableRow>
  )
}

type EditableTableHeaderProps = {
  inputProps: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
    onChange?: (value: string) => void
  }
  columnAccessor: string
  isReadOnly?: boolean
  width?: number | string
  isResizing?: boolean
  canResize?: boolean
  direction: 'left' | 'right'
  onResizeEnd?: ({
    width,
    accessor,
  }: {
    width?: number | string
    accessor: string
  }) => void
  table: TableType<TableData>
  tableType: 'parts-table' | 'tools-table' | 'generic-table'
  column: Column<TableData, unknown>
  headerSuggestions?: Array<{ key: string; value: string }>
  resizeHandler?: (event: unknown) => void
  saveChanges: () => void
  selectTable?: () => void
  addNewColumn?: (options: {
    accessorKey: string
    position: 'left' | 'right'
    newAccessorKey?: string
  }) => void
}
const EditableTableHeader = ({
  inputProps,
  columnAccessor,
  isReadOnly,
  width,
  isResizing,
  canResize,
  direction,
  table,
  tableType,
  column,
  headerSuggestions = [],
  resizeHandler,
  onResizeEnd,
  saveChanges,
  selectTable,
  addNewColumn,
}: EditableTableHeaderProps) => {
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const [hasChanged, setHasChanged] = useState(false)
  const { onChange, value, ...otherProps } = useMemo(
    () => inputProps,
    [inputProps],
  )
  const [search, setSearch] = useState((value as string) || '')
  const [open, setOpen] = useState(false)
  const accessor = (column.columnDef as any).accessorKey as string
  const tableMeta = table.options.meta as {
    selectedColumn: string | null
  }
  const isColumnSelected = useMemo(
    () => accessor === tableMeta.selectedColumn,
    [accessor, tableMeta.selectedColumn],
  )
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const filteredOptions = useMemo(
    () =>
      search
        ? headerSuggestions.filter(
            (option) =>
              option.value?.toLowerCase().includes(search?.toLowerCase?.()) &&
              option.value !== value,
          )
        : headerSuggestions,
    [search, value, headerSuggestions],
  )

  useEffect(() => {
    if (!search) setSearch(value as string)
  }, [value])

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [open])

  useEffect(() => {
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1)
  }, [filteredOptions])

  const handleOnBlur = () => {
    setOpen(false)
    if (hasChanged) {
      saveChanges()
      setHasChanged(false)
    }
  }

  const handleOnChange = (value: string) => {
    setHasChanged(true)
    if (onChange) {
      onChange(value)
    }
  }

  const onSelectOption = async (option: { key: string; value: string }) => {
    setSearch(option.value)
    handleOnChange(option.value)
    inputRef.current?.blur()
    await addNewColumn?.({
      accessorKey: accessor,
      position: 'right',
      newAccessorKey: option.key,
    })
  }

  const prevIsResizing = usePrevious(isResizing)

  useEffect(() => {
    if (!onResizeEnd) return
    if (prevIsResizing && !isResizing) {
      onResizeEnd({ width, accessor: columnAccessor })
    }
  }, [isResizing, width, columnAccessor, prevIsResizing, onResizeEnd])

  return (
    <TableHead
      className={cn(
        'relative items-center border-x border-solid border-gray-200 border-collapse text-xs h-auto py-1',
        {
          'bg-blue-100/50': isColumnSelected,
        },
      )}
      style={{ position: 'relative', width }}
      data-id-column={columnAccessor}
      data-id-row={-1}
      data-testid={`${tableType}__table-head`}
    >
      <Popover open={open}>
        <PopoverTrigger asChild>
          <div
            ref={containerRef}
            className="relative w-full flex items-center"
            data-id-column={columnAccessor}
            data-id-row={-1}
          >
            {/* 
              Disabling the Input conflicts when the react-dnd event system. 
              As a work around, we're going to rendering the column name when the component is disabled,
              if the component is enabled then we'll render the input component
            */}
            {isReadOnly || otherProps.disabled ? (
              search
            ) : (
              <Input
                {...otherProps}
                onBlur={handleOnBlur}
                value={search}
                onChange={(e) => {
                  const value = e.target.value
                  setSearch(value)
                  handleOnChange(value)
                }}
                onFocus={() => {
                  setOpen(true)
                  setHighlightedIndex(0)
                }}
                onMouseDownCapture={(e) => e.stopPropagation()}
                onClick={(e) => {
                  if (selectTable) selectTable()
                  e.stopPropagation()
                }}
                disabled={isReadOnly || otherProps.disabled}
                data-id-column={columnAccessor}
                data-id-row={-1}
                onKeyDown={(e) => {
                  if (!open || filteredOptions.length === 0) return

                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setHighlightedIndex(
                      (prev) => (prev + 1) % filteredOptions.length,
                    )
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setHighlightedIndex((prev) =>
                      prev <= 0 ? filteredOptions.length - 1 : prev - 1,
                    )
                  } else if (e.key === 'Enter') {
                    e.preventDefault()
                    if (highlightedIndex >= 0) {
                      const selected = filteredOptions[highlightedIndex]
                      void onSelectOption(selected)
                    }
                  }
                }}
              />
            )}
          </div>
        </PopoverTrigger>
        <PopoverPortal>
          {filteredOptions.length ? (
            <PopoverContent
              className="bg-white z-[1000] border rounded-lg shadow-lg p-1"
              onOpenAutoFocus={(e) => e.preventDefault()}
              sideOffset={4}
              align="start"
              style={{
                width: 'auto',
                minWidth: 150,
              }}
              onPointerDownCapture={(e) => e.preventDefault()}
            >
              <div className="max-h-40 overflow-y-auto">
                {filteredOptions.map((option, i) => (
                  <div
                    key={option.key}
                    className={cn(
                      'flex items-center justify-between px-2 py-1 rounded-md cursor-pointer text-sm',
                      highlightedIndex === i
                        ? 'bg-gray-200'
                        : 'hover:bg-gray-100',
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      void onSelectOption(option)
                    }}
                  >
                    {option.value}
                  </div>
                ))}
              </div>
            </PopoverContent>
          ) : null}
        </PopoverPortal>
      </Popover>
      {canResize && !isReadOnly && (
        <div
          onMouseDown={(e) => {
            e.stopPropagation()
            if (resizeHandler) resizeHandler(e)
          }}
          onTouchStart={(e) => {
            e.stopPropagation()
            if (resizeHandler) resizeHandler(e)
          }}
          className={cn(
            'absolute top-0 h-full w-0.5 bg-black/50 cursor-col-resize select-none touch-none opacity-0 hover:opacity-100',
            {
              'bg-sky-500': isResizing,
              'opacity-100': isResizing,
              'right-0': direction === 'left',
              'left-0': direction === 'right',
            },
          )}
        />
      )}
    </TableHead>
  )
}

type EditableTableCellProps = CellContext<TableData, unknown> & {
  onChange: (rowIndex: number, columnId: string | number, value: string) => void
  selectTable?: () => void
  isCustomRow?: boolean
  isReadOnly?: boolean
  documentPageId: string
  suggestions?: Array<string>
  tableType: 'parts-table' | 'tools-table' | 'generic-table'
}
const EditableTableCell = ({
  getValue,
  onChange,
  selectTable,
  row,
  column,
  table,
  tableType,
  isCustomRow = false,
  isReadOnly,
  documentPageId,
  suggestions,
}: EditableTableCellProps) => {
  const [hasChanged, setHasChanged] = useState(false)
  const [value, setValue] = useState<string>(getValue<string>())
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const accessor = (column.columnDef as any).accessorKey as string
  const rowIndex = row.index
  const externalValue = getValue<string>() || ''
  const tableMeta = table.options.meta as {
    selectedRow: number | null
    selectedColumn: string | null
  }
  const isRowSelected = useMemo(
    () => rowIndex === tableMeta.selectedRow,
    [rowIndex, tableMeta.selectedRow],
  )
  const isColumnSelected = useMemo(
    () => accessor === tableMeta.selectedColumn,
    [accessor, tableMeta.selectedColumn],
  )

  const adjustHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [textareaRef])

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries && entries.length) {
        adjustHeight()
      }
    })

    if (textareaRef.current) {
      observer.observe(textareaRef.current)
    } else {
      observer.disconnect()
    }

    return () => {
      observer.disconnect()
    }
  }, [textareaRef, adjustHeight])

  useEffect(() => {
    setTimeout(() => {
      adjustHeight()
    }, 100)
  }, [value, adjustHeight])

  useEffect(() => {
    if (!hasChanged && externalValue !== value) {
      setValue(externalValue)
    }
  }, [externalValue, hasChanged, value])

  const handleOnBlur = () => {
    if (!hasChanged) {
      return
    }

    // Commit local cell edits to table state once on blur (not per keypress).
    if (onChange) {
      onChange(row.index, column.id, value)
    }

    const meta = table.options.meta as {
      saveTableData: ({
        isCustomRow,
        rowIndex,
      }: {
        isCustomRow: boolean
        rowIndex: number
      }) => void
    }

    if (meta && typeof meta.saveTableData === 'function') {
      meta.saveTableData({
        isCustomRow,
        rowIndex: row.index,
      })
    }

    setHasChanged(false)
  }

  const handleOnChange = useCallback((val: string) => {
    setHasChanged(true)
    setValue(val)
  }, [])

  return (
    <TableCell
      id={createTableCellId(column, row, documentPageId)}
      className={cn(
        'border-x border-solid border-gray-200 border-collapse py-0 bg-white/70',
        {
          'bg-blue-100/50': isRowSelected || isColumnSelected,
        },
      )}
      style={{
        width: column.getSize(),
        position: 'relative',
      }}
      data-id-column={accessor}
      data-id-row={rowIndex}
      data-testid={`${tableType}__${accessor}-cell`}
    >
      {isReadOnly ? (
        <span
          className="text-xs inline-block"
          ref={textareaRef}
          data-id-column={accessor}
          data-id-row={rowIndex}
        >
          {value}
        </span>
      ) : suggestions?.length ? (
        <SearchableField
          values={suggestions}
          value={value}
          tableType={tableType}
          handleOnChange={handleOnChange}
          handleOnBlur={handleOnBlur}
          columnAccessor={accessor}
          rowIndex={rowIndex}
          onMouseDown={(e) => {
            if (selectTable) {
              selectTable()
            }
            e.stopPropagation()
          }}
        />
      ) : (
        <Textarea
          data-testid="custom-parts-table-cell-input"
          className="border-none shadow-none m-0 px-0 py-1 text-xs min-h-4 overflow-hidden resize-none"
          style={{
            outline: 'none',
            boxShadow: 'none',
          }}
          value={value}
          onChange={(e) => handleOnChange(e.target.value)}
          onBlur={handleOnBlur}
          onMouseDownCapture={(e) => {
            if (selectTable) {
              selectTable()
            }
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.stopPropagation()
          }}
          ref={textareaRef}
          data-id-column={accessor}
          data-id-row={rowIndex}
          rows={1}
        />
      )}
    </TableCell>
  )
}

type SearchableFieldProps = {
  values: Array<string>
  value: string
  columnAccessor?: string
  rowIndex?: number
  tableType: 'parts-table' | 'tools-table' | 'generic-table'
  handleOnChange: (val: string) => void
  handleOnBlur: () => void
  onMouseDown: (e: React.MouseEvent<Element, MouseEvent>) => void
}

const SearchableField = ({
  value = '',
  values,
  columnAccessor,
  rowIndex,
  tableType,
  handleOnChange,
  handleOnBlur,
  onMouseDown,
}: SearchableFieldProps) => {
  const [search, setSearch] = useState(value)
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const MAX_SUGGESTIONS = 50

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const filteredOptions = useMemo(() => {
    if (!open) return []

    const normalizedSearch = search?.trim()?.toLowerCase?.()
    if (!normalizedSearch) return []

    const options: string[] = []
    for (const option of values) {
      if (!option || option === value) continue
      if (option.toLowerCase().includes(normalizedSearch)) {
        options.push(option)
        // Bound suggestion rendering/filter cost for large BOM lists.
        if (options.length >= MAX_SUGGESTIONS) break
      }
    }

    return options
  }, [open, search, value, values])

  useEffect(() => {
    // Keep the input UI in sync with external cell value updates (e.g. undo/redo).
    setSearch(value || '')
  }, [value])

  useEffect(() => {
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1)
  }, [filteredOptions])

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <div ref={containerRef} className="relative w-full flex items-center">
          <Textarea
            className={cn(
              'border-none shadow-none m-0 px-0 py-1 text-xs min-h-4 overflow-hidden resize-none',
            )}
            style={{
              outline: 'none',
              boxShadow: 'none',
            }}
            value={search}
            onChange={(e) => {
              const value = e.target.value
              setOpen(!!value)
              setSearch(value)
              handleOnChange(value)
            }}
            onFocus={() => {
              setOpen(true)
              setHighlightedIndex(0)
            }}
            onBlur={() => {
              setOpen(false)
              setTimeout(() => handleOnBlur(), 100)
            }}
            onMouseDownCapture={onMouseDown}
            onClick={(e) => e.stopPropagation()}
            ref={textareaRef}
            rows={1}
            data-id-column={columnAccessor}
            data-id-row={rowIndex}
            onKeyDown={(e) => {
              if (!open || filteredOptions.length === 0) return

              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setHighlightedIndex(
                  (prev) => (prev + 1) % filteredOptions.length,
                )
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setHighlightedIndex((prev) =>
                  prev <= 0 ? filteredOptions.length - 1 : prev - 1,
                )
              } else if (e.key === 'Enter') {
                e.preventDefault()
                if (highlightedIndex >= 0) {
                  const selected = filteredOptions[highlightedIndex]
                  handleOnChange(selected)
                  setSearch(selected)
                  textareaRef.current?.blur()
                  setOpen(false)
                }
              }
            }}
          />
        </div>
      </PopoverTrigger>

      <PopoverPortal>
        <SearchableSuggestionsList
          values={filteredOptions}
          highlightedIndex={highlightedIndex}
          width={containerRef.current?.offsetWidth}
          tableType={tableType}
          onSelect={(option) => {
            handleOnChange(option)
            setSearch(option)
            setOpen(false)
            setTimeout(() => textareaRef.current?.blur())
          }}
        />
      </PopoverPortal>
    </Popover>
  )
}

const SearchableSuggestionsList = ({
  values,
  highlightedIndex,
  width,
  tableType,
  onSelect,
}: {
  values: Array<string>
  highlightedIndex?: number
  width?: number | string
  tableType: 'parts-table' | 'tools-table' | 'generic-table'
  onSelect: (value: string) => void
}) => {
  return values.length > 0 ? (
    <PopoverContent
      className="bg-white z-[1000] border rounded-lg shadow-lg p-1"
      onOpenAutoFocus={(e) => e.preventDefault()}
      sideOffset={4}
      align="start"
      style={{
        width,
      }}
      onPointerDownCapture={(e) => e.preventDefault()}
      data-testid={`${tableType}__suggestion-list`}
    >
      <div className="max-h-40 overflow-y-auto">
        {values.map((option, i) => (
          <div
            data-testid="suggestion-list-option"
            key={i}
            className={cn(
              'flex items-center justify-between px-2 py-1 rounded-md cursor-pointer text-sm',
              highlightedIndex === i ? 'bg-gray-200' : 'hover:bg-gray-100',
            )}
            onClick={(e) => {
              e.preventDefault()
              onSelect(option)
            }}
          >
            {option}
          </div>
        ))}
      </div>
    </PopoverContent>
  ) : (
    <></>
  )
}

const createTableCellId = (
  column: Column<TableData, unknown>,
  row: Row<TableData>,
  documentPageId: string,
) => {
  if (column.id === 'partName') {
    return `part-${documentPageId}-${row.original.partName}`
  }
}
